import { inject, Injectable } from '@angular/core';
import { Login } from '@app/core/auth/login';
import { ConfigurationService } from '@app/core/config/configuration.service';
import { LoginService } from '@app/shared/services/login.service';
import Keycloak from 'keycloak-js';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { ActivatedRouteSnapshot, Router, UrlTree } from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly configurationService = inject(ConfigurationService);
  private readonly loginService = inject(LoginService);
  private readonly router = inject(Router);

  private keycloak       : Keycloak | null = null;
  private tokenTimer     : NodeJS.Timeout | null = null;
  private readonly authenticatedSubject = new BehaviorSubject<boolean>(false);
  private user           : Login | null = null;
  public onTokenExpired ?: () => void;
  private logoutPage     : string = '';

  get authenticated$(): Observable<boolean> {
    return this.authenticatedSubject;
  }

  isAuthenticated(): boolean {
    return this.authenticatedSubject.getValue();
  }

  async init(): Promise<void> {
    const config = this.configurationService.getConfig();
    this.logoutPage = config.logoutPage;

    const keycloakConfig: Keycloak.KeycloakConfig = {
      url: config.ssoUrl,
      realm: config.ssoRealm,
      clientId: config.ssoClient
    };

    // Login via eIAM -> BLV-Mitarbeiter

    const initOptions: Keycloak.KeycloakInitOptions = {
      pkceMethod       : 'S256',
      onLoad           : 'login-required',
      checkLoginIframe : false,
      refreshToken     : localStorage.getItem('refresh_token') ?? undefined,
      idToken          : localStorage.getItem('id_token')      ?? undefined,
      token            : localStorage.getItem('access_token')  ?? undefined
    };

    if (this.isISAbv()) {
      let token = this.getISAbvToken();
      let username = this.extractUsernameFromToken(token);

      if (token && username) {
        let json = await fetch(`${config.ssoUrl}/realms/${config.ssoRealm}/protocol/openid-connect/token`, {
          method: 'POST',
          headers: { 'content-type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type : 'password',
            username   : username,
            password   : token,
            client_id  : config.ssoClient,
            scope      : 'openid'
          })
        }).then(response => response.json());

        initOptions.refreshToken = json.refresh_token;
        initOptions.idToken = json.id_token;
        initOptions.token = json.access_token;
      } else {
        console.error("Token couldn't be initialized");
      }
    }

    this.keycloak = new Keycloak(keycloakConfig);
    this.keycloak.onAuthSuccess = () => this.storeAuthorization();
    this.keycloak.onAuthRefreshSuccess = () => this.startTokenTimer();
    this.keycloak.onTokenExpired = () => this.tokenExpired();
    this.keycloak.onAuthLogout = () => {
      this.clearAuthorization();
      this.authenticatedSubject.next(false);
    };

    // Keycloak hinterlegen fürs Backend-/Frontend-Test
    globalThis.keycloak = this.keycloak;

    const authenticated = await this.keycloak.init(initOptions);
    if (authenticated) {
      await this.initUser();
    }

    // LocalStorage Elemente löschen, die nicht nach einem Refresh erhalten bleiben sollen
    this.clearLocalStorage();
  }

  isISAbv() {
    return location.pathname.startsWith('/extern');
  }

  getISAbvToken(): string | undefined {
    // TODO Besser mit Angular API umsetzen
    let regex = /^\?jwt=(?<token>.*)$/;
    let result = regex.exec(location.search)
    if (!result) {
      regex = /^\/extern\/(?<token>.*)$/;
      result = regex.exec(location.pathname);
    }
    return result?.groups?.token;
  }

  extractUsernameFromToken(token?: string): string | undefined {
    let regex = /^(?<header>[\w-]+)\.(?<payload>[\w-]+)\.(?<signature>[\w-]+$)/
    let payload = token ? regex.exec(token)?.groups?.payload : undefined;
    let base64 = payload?.replaceAll('-', '+')?.replaceAll('_', '/');
    let json = base64 ? this.unicode_atob(base64) : undefined;
    let data = json ? JSON.parse(json) : undefined;
    return data ? JSON.stringify({sub: data.sub}) : undefined;
  }

  private unicode_atob(base64: string): string {
    // Bugfix: atob kann nicht mit Base64 encoded Unicode-Strings umgehen
    //         https://stackoverflow.com/a/62537645/11291109
    return decodeURIComponent(escape(atob(base64))); // NOSONAR (siehe oben)
  }

  private async initUser(): Promise<void> {
    const token = this.keycloak?.tokenParsed as any;

    // Sprache auslesen
    // Wenn die Sprache nicht im Token geliefert wird, nehmen wir die Browsersprache
    let sprache = token.lang ?? navigator.language;

    const user: Login = {
      userId: token.preferred_username,
      vorname: token.given_name,
      nachname: token.family_name,
      email: token.email,
      anschrift: token.address,
      uid: token.uid,
      zusatz: token.appendix,
      burNr: token.bur,
      language: sprache
    };

    const response = await firstValueFrom(this.loginService.get(user));

    // Wenn der Benutzer undefined ist oder die Inhaltsrolle nicht erhält,
    // wird er auf die Zugriffsantragsseite weitergeleitet
    if (response.user === undefined) {
      const config = this.configurationService.getConfig();
      // Keycloak Logout durchführen
      await fetch(`${config.ssoUrl}/realms/${config.ssoRealm}/protocol/openid-connect/logout`)
        .then(response => console.log('Keycloak logout successful?', response.ok));

      // Weiterleiten
      globalThis.location.href = config.ssoAccessReqUrl.replace('${ssoClient}', config.ssoClient).replace('${returnURL}', config.rootUrl);
    } else if (this.isISAbv() && response.user.language !== sprache) {
      // Beim ISABV-Login die Sprache aus dem Token übernehmen
      response.user.language = sprache;
      this.user = await firstValueFrom(this.loginService.changeLanguage(response.user));
      this.user.roles = response.roles;
      this.authenticatedSubject.next(true);
    } else {
      this.user = response.user;
      this.user.roles = response.roles;
      this.authenticatedSubject.next(true);
    }
  }

  checkLoginState(): Promise<boolean> {
    if (!this.keycloak?.authenticated) {
      return Promise.resolve(false);
    }
    return this.refreshToken()
      .then(() => !! this.keycloak?.authenticated);
  }

  getAccessToken(): Promise<string | undefined> {
    return this.refreshToken()
      .then(() => this.keycloak?.token);
  }

  refreshToken(minValidity = 10): Promise<boolean> {
    if (this.keycloak) {
      return this.keycloak.updateToken(minValidity);
    }
    return Promise.resolve(false);
  }

  getUser(): Login | null {
    return this.user;
  }

  logout(): Promise<void> {
    if (this.keycloak) {
      return this.keycloak.logout({redirectUri: this.logoutPage});
    }
    this.clearAuthorization();
    return Promise.resolve();
  }

  goToMyAccount(): void {
    const config = this.configurationService.getConfig();
    const url = config.ssoAccountUrl.replace('${returnURL}', encodeURIComponent(location.href));
    window.open(url, '_blank');
  }

  private clearLocalStorage() {
    localStorage.removeItem('filter');
  }

  private storeAuthorization() {
    localStorage.setItem('access_token',  this.keycloak?.token        ?? '');
    localStorage.setItem('refresh_token', this.keycloak?.refreshToken ?? '');
    localStorage.setItem('id_token',      this.keycloak?.idToken      ?? '');

    this.startTokenTimer();
  }

  private clearAuthorization() {
    this.user = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');
    this.keycloak?.clearToken();
    this.clearTokenTimer();
  }

  private startTokenTimer(): void {
    this.clearTokenTimer();

    const issuedAt = this.keycloak?.tokenParsed?.iat ?? 0;
    const expiresAt = this.keycloak?.tokenParsed?.exp ?? 0;
    const expirationTimeout = expiresAt - issuedAt;

    // 20 Sekunden vor Ablauf das Token erneuern
    const nextRefresh = Math.max(0, expirationTimeout - 20);
    this.tokenTimer = setTimeout(() => this.refreshToken(20), nextRefresh * 1000);
  }

  private clearTokenTimer(): void {
    if (this.tokenTimer) {
      clearTimeout(this.tokenTimer);
    }
    this.tokenTimer = null;
  }

  private tokenExpired(): void {
    if (this.onTokenExpired) {
      this.onTokenExpired();
    }
  }

  canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    return this.checkLoginState()
      .then( authenticated => authenticated || this.generateUrlTree(route, '/unauthorized') );
  }

  generateUrlTree(route: ActivatedRouteSnapshot, url: string): UrlTree {
    return this.router.createUrlTree([url], {
      queryParams: {
        url: this.router.serializeUrl(
          this.router.createUrlTree(
            route.pathFromRoot
              .reduce<any[]>((segments, current) => segments.concat(current.url), [])
              .map(value => value.toString()),
            {
              queryParams : route.queryParams,
              fragment    : route.fragment ?? undefined
            }
          )
        )
      }
    });
  }
}
